/**
 * A fila de saída do aparelho.
 *
 * É a peça mais importante do tablet — mais que qualquer tela. Todo trabalho
 * que o operador faz vira uma linha aqui ANTES de qualquer tentativa de rede,
 * e só sai daqui quando o servidor confirma. É isso que permite dizer, com
 * honestidade, que nada se perde.
 *
 * TRÊS DECISÕES QUE SUSTENTAM ISSO
 *
 * 1. A chave do evento nasce no APARELHO, junto com a ação — nunca no envio.
 *    Se nascesse no envio, uma repetição por rede ruim geraria chave nova e o
 *    servidor trataria como apontamento diferente: etapa concluída duas vezes,
 *    tempo dobrado, e ninguém descobre até o relatório do mês não fechar.
 *
 * 2. Envio em SÉRIE, um por vez, na ordem em que aconteceram. Paralelo seria
 *    mais rápido e erraria a ordem: a conclusão chegaria antes do início, e o
 *    escritório veria etapa terminada sem ter começado.
 *
 * 3. Recuo crescente com teto. Rede ruim não melhora sendo martelada, e um
 *    aplicativo que tenta sem parar torra a bateria do tablet antes do
 *    meio-dia.
 *
 * O QUE É "TRAVADO": erro que tentar de novo não resolve (etapa que não existe
 * mais, operador desligado do cadastro). Fica visível para o encarregado
 * decidir, em vez de girar para sempre em silêncio.
 *
 * (Mesmo desenho do `fila.ts` do Nexo em Campo, e pelas mesmas razões.)
 */

import type { Deposito } from '@pcp/dados';
import { novoId } from '@pcp/dominio';

/** Recuo: 5 s, 15 s, 45 s, 2 min, 5 min, 15 min — e para de crescer aí. */
const RECUOS_MS = [5_000, 15_000, 45_000, 120_000, 300_000, 900_000] as const;

export const TENTATIVAS_ATE_TRAVAR = 8;

export function recuoDe(tentativas: number): number {
  const i = Math.min(Math.max(tentativas - 1, 0), RECUOS_MS.length - 1);
  return RECUOS_MS[i] ?? RECUOS_MS[RECUOS_MS.length - 1]!;
}

/**
 * Erros que NÃO adianta repetir. São decisões do servidor, não falha de rede:
 * repetir só gastaria bateria e esconderia o problema de quem pode resolver.
 */
export function ehDefinitivo(mensagem: string): boolean {
  const m = mensagem.toLowerCase();
  return (
    m.includes('não existe mais') ||
    m.includes('nao existe mais') ||
    m.includes('não encontrad') ||
    m.includes('nao encontrad') ||
    m.includes('não está liberado') ||
    m.includes('nao esta liberado') ||
    m.includes('está inativo') ||
    m.includes('esta inativo') ||
    m.includes('cancelada') ||
    m.includes('sem acesso') ||
    m.includes('inválid') ||
    m.includes('invalid')
  );
}

export type EstadoDoItem = 'pendente' | 'travado';

export interface ItemDaFila {
  /** A chave do evento. Nasce no aparelho, junto com a ação. */
  id: string;
  tipo: string;
  carga: unknown;
  criadoEm: string;
  tentativas: number;
  proximaTentativaEm: string;
  estado: EstadoDoItem;
  ultimoErro: string | null;
}

export interface OpcoesDaFila {
  deposito: Deposito;
  /** Injetado para o teste poder controlar o tempo. */
  agora?: () => number;
  /** Injetado para o teste poder prever a chave. */
  novaChave?: () => string;
}

export class Fila {
  private deposito: Deposito;
  private agora: () => number;
  private novaChave: () => string;

  constructor(o: OpcoesDaFila) {
    this.deposito = o.deposito;
    this.agora = o.agora ?? (() => Date.now());
    this.novaChave = o.novaChave ?? novoId;
  }

  /**
   * Enfileira uma ação. Devolve a chave — que é a mesma do evento gravado
   * localmente, para os dois lados falarem do mesmo fato.
   */
  async enfileirar(tipo: string, carga: unknown, id?: string): Promise<string> {
    const chave = id ?? this.novaChave();
    const agora = new Date(this.agora()).toISOString();
    const item: ItemDaFila = {
      id: chave,
      tipo,
      carga,
      criadoEm: agora,
      tentativas: 0,
      proximaTentativaEm: agora,
      estado: 'pendente',
      ultimoErro: null,
    };
    await this.deposito.gravar('filaSaida', item);
    return chave;
  }

  /** Os itens prontos para ir agora, na ordem em que aconteceram. */
  async prontos(): Promise<ItemDaFila[]> {
    const agora = this.agora();
    const todos = await this.deposito.listar<ItemDaFila>('filaSaida');
    return todos
      .filter((i) => i.estado === 'pendente' && Date.parse(i.proximaTentativaEm) <= agora)
      .sort((a, b) => a.criadoEm.localeCompare(b.criadoEm) || a.id.localeCompare(b.id));
  }

  async contarPendentes(): Promise<number> {
    const todos = await this.deposito.listar<ItemDaFila>('filaSaida');
    return todos.filter((i) => i.estado === 'pendente').length;
  }

  async contarTravados(): Promise<number> {
    const todos = await this.deposito.listar<ItemDaFila>('filaSaida');
    return todos.filter((i) => i.estado === 'travado').length;
  }

  async travados(): Promise<ItemDaFila[]> {
    const todos = await this.deposito.listar<ItemDaFila>('filaSaida');
    return todos.filter((i) => i.estado === 'travado');
  }

  /** O servidor confirmou: o item some da fila. */
  async confirmar(id: string): Promise<void> {
    await this.deposito.apagar('filaSaida', id);
  }

  /** Falhou. Decide entre tentar de novo mais tarde e travar. */
  async falhar(id: string, erro: string): Promise<'adiado' | 'travado'> {
    const item = await this.deposito.ler<ItemDaFila>('filaSaida', id);
    if (!item) return 'adiado';

    const tentativas = item.tentativas + 1;
    const travar = ehDefinitivo(erro) || tentativas >= TENTATIVAS_ATE_TRAVAR;

    await this.deposito.gravar<ItemDaFila>('filaSaida', {
      ...item,
      tentativas,
      ultimoErro: erro,
      estado: travar ? 'travado' : 'pendente',
      proximaTentativaEm: new Date(this.agora() + recuoDe(tentativas)).toISOString(),
    });
    return travar ? 'travado' : 'adiado';
  }

  /** O encarregado mandou tentar de novo. */
  async destravar(id: string): Promise<void> {
    const item = await this.deposito.ler<ItemDaFila>('filaSaida', id);
    if (!item) return;
    await this.deposito.gravar<ItemDaFila>('filaSaida', {
      ...item,
      estado: 'pendente',
      tentativas: 0,
      proximaTentativaEm: new Date(this.agora()).toISOString(),
    });
  }

  /**
   * O encarregado decidiu descartar. Só isto apaga um apontamento sem ele ter
   * chegado ao servidor — por isso exige motivo, que fica no registro.
   */
  async descartar(id: string, _motivo: string): Promise<void> {
    await this.deposito.apagar('filaSaida', id);
  }
}
